import { cn, formatWithNonBreakingSpaces } from '@workspace/utils';
import React, { useRef } from 'react';
import { useGlobalForm } from '../contexts/global-form-context';
import type { ChildrenProps } from '../types';
import { Transition } from './animation';
import { FieldRadioGroup, SmartButton } from './smart-form';

// Type pour les props du FieldRadioGroup
interface FieldRadioGroupProps {
    onClick?: () => void;
    [key: string]: any; // Pour permettre d'autres propriétés
}

export interface QuestionContainerProps {
    active?: boolean;
    nextQuestion?: () => void;
    children?: React.ReactNode;
    index?: number;
    totalQuestions?: number;
    className?: string;
    buttonLabel?: string;
    disabledButton?: boolean;
    hideButton?: boolean;
    disabledAutoNext?: boolean;
}

export function Question({
    active = true,
    index = 0,
    children,
    buttonLabel = 'Continuer',
    className,
    disabledButton,
    disabledAutoNext = false,
    hideButton = false,
}: QuestionContainerProps) {
    const g = useGlobalForm();
    const buttonRef = useRef<any>(null);

    const visible = g.meta.subStep === index;

    return (
        <div
            className={cn('step-question w-full', className)}
            data-active={active}
            data-index={index}
            data-visible={visible}
        >
            <Transition
                appear
                as="div"
                className={cn('w-full flex flex-col items-center space-y-4', className)}
                enter="transition-opacity duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                show={active ? visible : undefined}
            >
                {active && visible ? (
                    <>
                        {React.Children.map(children, (child) => {
                            // Vérifier si l'élément est valide et de type React.ReactElement
                            if (React.isValidElement(child) && !disabledAutoNext) {
                                // Vérifier si le type de l'enfant est FieldRadioGroup
                                if (child.type === FieldRadioGroup) {
                                    // Approche sans spread, en utilisant Object.assign pour créer un nouvel objet de props
                                    const newProps = Object.assign({}, child.props, {
                                        onClick: () => setTimeout(() => buttonRef.current?.click(), 100),
                                    }) as FieldRadioGroupProps;

                                    return React.cloneElement(child, newProps);
                                }
                                return child;
                            }
                            return child;
                        })}

                        <SmartButton
                            className={cn(hideButton && 'hidden', 'mt-6')}
                            disabled={disabledButton}
                            ref={buttonRef}
                        >
                            {buttonLabel}
                        </SmartButton>
                    </>
                ) : null}
            </Transition>
        </div>
    );
}

Question.Header = function QuestionHeader({ children, className }: ChildrenProps) {
    return <div className={cn('flex flex-col items-center text-center space-y-1', className)}>{children}</div>;
};

Question.Title = function QuestionTitle({ children, className }: ChildrenProps) {
    return <h1 className={cn('text-base sm:text-2xl whitespace-pre-line', className)}>{formatWithNonBreakingSpaces(children)}</h1>;
};

Question.SubTitle = function QuestionSubTitle({ children, className }: ChildrenProps) {
    return <h3 className={cn('text-sm sm:text-base text-primary font-medium', className)}>{formatWithNonBreakingSpaces(children)}</h3>;
};

Question.Description = function QuestionDescription({ children, className }: ChildrenProps) {
    return <div className={cn('space-y-2 mt-3 sm:mt-5 sm:text-lg leading-relaxed text-center', className)}>{formatWithNonBreakingSpaces(children)}</div>;
};
